import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  TemplateRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { chooseUnknownFormat, unknownExtension } from '../../engine/unknown-format';
import { resolveExtension, validateExtensionMap } from '../../formats/extension-rules';
import { prefersText } from '../../formats/text-format';
import type {
  ArchiveFormat,
  ArchiveLoadedEvent,
  ArchiveSelection,
  DocumentContentsVisibleChangeEvent,
  DocumentErrorEvent,
  DocumentFormat,
  DocumentFormatSelectedEvent,
  DocumentLoadedEvent,
  DocumentProgressEvent,
  DocumentSource,
  DocumentView,
  DocumentViewChangeEvent,
  DocumentWordWrapChangeEvent,
  ExtensionMap,
  SizeUnits,
  UnknownFileBehavior,
  ViewerMemoryStats
} from '../../models/viewer-types';
import { ArchiveTocComponent } from '../archive-toc/archive-toc.component';
import { DocumentViewerComponent } from '../document-viewer/document-viewer.component';

const ARCHIVE_FORMATS: ReadonlyArray<DocumentFormat> = ['zip', 'msp360-log-archive'];

// A type predicate rather than a plain check: the branches rely on the format being narrowed, which
// `includes` on its own does not do.
function isArchiveFormat(format: DocumentFormat): format is ArchiveFormat {
  return ARCHIVE_FORMATS.includes(format);
}

@Component({
  selector: 'mbs-document-browser',
  standalone: true,
  imports: [ArchiveTocComponent, DocumentViewerComponent],
  encapsulation: ViewEncapsulation.ShadowDom,
  templateUrl: './document-browser.component.html',
  styleUrls: ['../../styles/theme.scss', './document-browser.component.scss', '../../styles/dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentBrowserComponent implements OnDestroy {
  readonly url = input<string | null>(null);
  readonly file = input<File | Blob | null>(null);
  readonly source = input<DocumentSource | null>(null);
  readonly fileName = input('');
  readonly format = input<DocumentFormat>('auto');
  readonly language = input('');
  readonly unknownFileBehavior = input<UnknownFileBehavior>('prompt');
  readonly view = input<DocumentView>('auto');
  readonly emptyStateTemplate = input<TemplateRef<unknown> | null>(null);
  readonly contentsVisible = input(false);
  readonly wordWrap = input(false);
  readonly sizeUnits = input<SizeUnits>('binary');
  readonly extensionMap = input<ExtensionMap>({});
  readonly maxFileBytes = input(30 * 1024 * 1024);
  readonly maxPdfBytes = input(100 * 1024 * 1024);
  readonly maxArchiveBytes = input(1024 * 1024 * 1024);
  readonly maxExpandedBytes = input<number | null>(null);
  readonly timeoutMs = input(60000);
  readonly pdfAssetsUrl = input('./pdfjs/');
  readonly noticesUrl = input('');
  readonly wordWrapChange = output<DocumentWordWrapChangeEvent>();
  readonly contentsVisibleChange = output<DocumentContentsVisibleChangeEvent>();
  readonly viewChange = output<DocumentViewChangeEvent>();
  readonly formatSelected = output<DocumentFormatSelectedEvent>();
  readonly loaded = output<DocumentLoadedEvent>();
  readonly progressChange = output<DocumentProgressEvent>();
  readonly loadError = output<DocumentErrorEvent>();
  readonly entrySelected = output<ArchiveSelection>();

  private readonly layout = viewChild<ElementRef<HTMLElement>>('layout');
  readonly tocWidth = signal(270);
  readonly resizing = signal(false);
  private drag?: { id: number; x: number; width: number; target: HTMLElement };
  maxTocWidth(): number {
    return Math.max(160, Math.min(1000, (this.layout()?.nativeElement.clientWidth || 800) - 260));
  }
  private setTocWidth(width: number): void {
    this.tocWidth.set(Math.round(Math.max(160, Math.min(this.maxTocWidth(), width))));
  }
  startResize(event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    this.drag = {
      id: event.pointerId,
      x: event.clientX,
      width: this.tocWidth(),
      target
    };
    target.setPointerCapture(event.pointerId);
    this.resizing.set(true);
  }
  resize(event: PointerEvent): void {
    if (this.drag?.id === event.pointerId) this.setTocWidth(this.drag.width + event.clientX - this.drag.x);
  }
  stopResize(): void {
    const drag = this.drag;
    this.drag = undefined;
    this.resizing.set(false);
    if (drag?.target.hasPointerCapture(drag.id)) drag.target.releasePointerCapture(drag.id);
  }
  resizeKey(event: KeyboardEvent): void {
    const values: Record<string, number> = {
      ArrowLeft: this.tocWidth() - 16,
      ArrowRight: this.tocWidth() + 16,
      Home: 160,
      End: this.maxTocWidth()
    };
    if (event.key in values) {
      event.preventDefault();
      this.setTocWidth(values[event.key]);
    }
  }
  @HostListener('window:resize') onWindowResize(): void {
    if ((this.layout()?.nativeElement.clientWidth ?? 0) > 700) this.setTocWidth(this.tocWidth());
  }
  private readonly viewer = viewChild(DocumentViewerComponent);
  private readonly toc = viewChild(ArchiveTocComponent);
  readonly archiveMode = signal(false);
  readonly overview = signal(false);
  readonly archiveName = signal('');
  readonly error = signal('');
  private readonly platformId = inject(PLATFORM_ID);
  private ready = false;
  private version = 0;
  private controller?: AbortController;

  /**
   * What is to be opened, and the settings it is opened under. The inputs that only travel on to the
   * children -- how sizes read, which view, word wrap -- are deliberately not read here: changing
   * one of them must not reopen the document.
   */
  private readonly openRequest = computed(() => ({
    source: this.source(),
    file: this.file(),
    url: this.url(),
    fileName: this.fileName(),
    format: this.format(),
    language: this.language(),
    extensionMap: this.extensionMap(),
    maxFileBytes: this.maxFileBytes(),
    maxPdfBytes: this.maxPdfBytes(),
    maxArchiveBytes: this.maxArchiveBytes(),
    maxExpandedBytes: this.maxExpandedBytes(),
    timeoutMs: this.timeoutMs(),
    pdfAssetsUrl: this.pdfAssetsUrl()
  }));

  constructor() {
    effect(() => {
      const layout = this.layout();

      this.openRequest();

      untracked(() => {
        this.ready = Boolean(layout) && isPlatformBrowser(this.platformId);

        if (!this.ready) return;

        // The children are told what to open through their own bindings, which have not been applied
        // yet when this runs; a turn later they have.
        queueMicrotask(() => {
          if (this.ready) this.applySource();
        });
      });
    });
  }

  private applySource(): void {
    const { source, file, url, fileName } = this.openRequest();

    if (source) void this.openSource(source);
    else if (file) void this.openFile(file, fileName);
    else if (url?.trim()) void this.openUrl(url, fileName);
    else this.clear();
  }
  clear(): void {
    this.stopResize();
    this.selectionController?.abort();
    this.lastArchiveSelection = undefined;
    this.version++;
    this.controller?.abort();
    this.viewer()?.clear();
    this.toc()?.clear();
    this.archiveMode.set(false);
    this.overview.set(false);
    this.error.set('');
  }
  private report(error: unknown): void {
    const message = error instanceof Error ? error.message : 'Unable to open document.';
    this.error.set(message);
    this.loadError.emit({ code: 'load_failed', message });
  }
  private async resolveUnknown(
    name: string,
    format: DocumentFormat,
    language: string,
    signal: AbortSignal,
    allowArchive = false
  ): Promise<{
    format: DocumentFormat;
    language: string;
    automatic: boolean;
  } | null> {
    const rule = resolveExtension(name, this.extensionMap(), format, language);
    const extension = unknownExtension(name, this.extensionMap(), format);
    if (!extension) return { ...rule, automatic: format === 'auto' };
    const choice = await chooseUnknownFormat(this.layout()?.nativeElement as HTMLElement, name, extension, {
      behavior: this.unknownFileBehavior(),
      allowArchive,
      signal,
      onSelect: (event) => this.formatSelected.emit(event)
    });
    return choice
      ? {
          format: choice as DocumentFormat,
          language,
          automatic: choice === 'auto'
        }
      : null;
  }
  private activeArchiveFormat: ArchiveFormat = 'zip';
  async openUrl(value: string, fileName = '', format: DocumentFormat = this.format(), language = this.language()): Promise<void> {
    if (!this.ready) return;
    this.clear();
    const version = this.version;
    const controller = new AbortController();

    this.controller = controller;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      validateExtensionMap(this.extensionMap());
      const url = new URL(value, document.baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) throw Error('Please use an HTTP or HTTPS URL.');
      if (location.protocol === 'https:' && url.protocol === 'http:') throw Error('An HTTPS page requires an HTTPS document URL.');
      let name = fileName || 'document';
      if (!fileName) {
        try {
          name = decodeURIComponent(url.pathname.split('/').pop() || name);
        } catch {
          /* Keep the fallback name for malformed URL escapes. */
        }
      }
      let rule = await this.resolveUnknown(name, format, language, controller.signal, true);
      if (!rule || !this.ready || version !== this.version) return;
      timer = setTimeout(() => controller.abort(), this.timeoutMs());
      if (rule.format === 'auto' && !prefersText(name) && !/\.(?:doc|xls|ppt|pptx)$/i.test(name)) {
        const response = await fetch(url.href, {
          headers: { Range: 'bytes=0-1023' },
          signal: controller.signal,
          credentials: 'omit',
          referrerPolicy: 'no-referrer'
        });
        const mime = (response.headers.get('content-type') || '').split(';')[0].toLowerCase();
        let zipSignature = false;
        if (response.body) {
          const reader = response.body.getReader();
          const prefix: number[] = [];
          try {
            while (prefix.length < 4) {
              const { done, value } = await reader.read();
              if (done) break;
              for (const byte of value.subarray(0, 4 - prefix.length)) prefix.push(byte);
            }
            zipSignature =
              prefix[0] === 80 && prefix[1] === 75 && ((prefix[2] === 3 && prefix[3] === 4) || (prefix[2] === 5 && prefix[3] === 6));
          } finally {
            await reader.cancel();
          }
        }
        if (zipSignature || ['application/zip', 'application/x-zip-compressed'].includes(mime))
          rule = { format: 'zip', language, automatic: true };
      }
      if (!this.ready || version !== this.version) return;
      if (controller.signal.aborted) throw Error('The request timed out. Please try again.');
      if (isArchiveFormat(rule.format)) {
        this.activeArchiveFormat = rule.format;
        this.archiveMode.set(true);
        this.overview.set(true);
        this.archiveName.set(name);
        await this.toc()?.openUrl(url.href, name, rule.format);
      } else await this.viewer()?.openUrl(url.href, fileName, rule.format, rule.language, rule.automatic);
    } catch (error) {
      if (this.ready && version === this.version)
        this.report(controller.signal.aborted ? Error('The request timed out. Please try again.') : error);
    } finally {
      clearTimeout(timer);
    }
  }
  async openFile(file: File | Blob, name = '', format: DocumentFormat = this.format(), language = this.language()): Promise<void> {
    if (!this.ready) return;
    this.clear();
    const version = this.version;
    const controller = new AbortController();
    this.controller = controller;
    try {
      validateExtensionMap(this.extensionMap());
      const rule = await this.resolveUnknown(name || (file instanceof File ? file.name : 'document'), format, language, controller.signal);
      if (!rule || !this.ready || version !== this.version) return;
      await this.viewer()?.openFile(file, name, rule.format, rule.language, rule.automatic);
    } catch (error) {
      if (this.ready && version === this.version) this.report(error);
    }
  }
  async openSource(source: DocumentSource): Promise<void> {
    if (!this.ready) return;
    this.clear();
    const version = this.version;
    const controller = new AbortController();
    this.controller = controller;
    try {
      validateExtensionMap(this.extensionMap());
      const rule = await this.resolveUnknown(
        source.name,
        source.format && source.format !== 'auto' ? source.format : this.format(),
        source.language ?? this.language(),
        controller.signal
      );
      if (!rule || !this.ready || version !== this.version) return;
      if (isArchiveFormat(rule.format)) throw Error('Archive browsing requires a ZIP URL.');
      await this.viewer()?.openSource({
        name: source.name,
        automaticFormat: source.automaticFormat ?? rule.automatic,
        size: source.size,
        format: rule.format,
        language: rule.language,
        load: (signal) => source.load(signal),
        ...(source.readRange
          ? {
              readRange: (offset: number, length: number, signal: AbortSignal) => source.readRange!(offset, length, signal)
            }
          : {})
      });
    } catch (error) {
      if (this.ready && version === this.version) this.report(error);
    }
  }
  archiveLoaded(event: ArchiveLoadedEvent): void {
    this.loaded.emit({
      type: this.activeArchiveFormat,
      fileName: event.fileName,
      size: event.size,
      entries: event.entries.filter((entry) => !entry.directory).length
    });
  }
  private lastArchiveSelection?: ArchiveSelection;
  async select(event: ArchiveSelection): Promise<void> {
    const version = this.version;
    this.selectionController?.abort();
    const controller = new AbortController();
    this.selectionController = controller;
    try {
      const rule = await this.resolveUnknown(
        event.source.name,
        event.source.format || 'auto',
        event.source.language || '',
        controller.signal
      );
      if (!this.ready || version !== this.version || controller.signal.aborted) return;
      if (!rule) {
        this.toc()?.selectEntry(this.lastArchiveSelection?.entry.id ?? null);
        return;
      }
      if (isArchiveFormat(rule.format)) throw Error('Nested archives are not supported.');
      const selection: ArchiveSelection = {
        ...event,
        source: {
          ...event.source,
          automaticFormat: unknownExtension(event.source.name, this.extensionMap()) ? rule.automatic : true,
          format: rule.format,
          language: rule.language
        }
      };
      this.lastArchiveSelection = selection;
      this.overview.set(false);
      this.entrySelected.emit(selection);
      await this.viewer()?.openSource(selection.source);
    } catch (error) {
      if (this.ready && version === this.version && !controller.signal.aborted) this.report(error);
    }
  }
  private selectionController?: AbortController;
  showOverview(): void {
    this.selectionController?.abort();
    this.lastArchiveSelection = undefined;
    this.viewer()?.clear({ preserveTextPreferences: true });
    this.overview.set(true);
  }
  getMemoryStats(): ViewerMemoryStats | null {
    return this.viewer()?.getMemoryStats() ?? null;
  }
  ngOnDestroy(): void {
    this.stopResize();
    this.ready = false;
    this.selectionController?.abort();
    this.version++;
    this.controller?.abort();
  }
}
